/**
 * Carrega a malha do avatar. three.js PURO (ADR-0003) — NEVER importe react aqui.
 *
 * O `GLTFLoader` entra por `import()` dinâmico, e isso NEVER é enfeite: ele e o
 * seu parser são ~30 KB que o jogador com a flag desligada NEVER deveria baixar,
 * e `BUD-bundle` conta o js INICIAL. Mesma disciplina do HUD de performance.
 *
 * Este arquivo NEVER decide número visual: altura vem de `@game/design`
 * (ART-DIRECTION M1), e o resto é lido da própria malha.
 */
import { tokens } from '@game/design';
import type { BufferGeometry, Mesh } from 'three';

/**
 * Normaliza a malha para o espaço do jogo: base no chão (`y = 0`), centrada em
 * XZ, e altura igual à do token.
 *
 * Existe porque o espaço de AUTORIA NEVER é o do jogo — esta malha vem centrada
 * na origem em Y, então metade dela ficaria enterrada no piso. Fazer isso uma vez
 * no carregamento é diferente de fazer por frame: o `Mesh` de cada jogador
 * compartilha esta geometria, então a conta acontece UMA vez para a sala inteira.
 */
export function normalizeAvatarGeometry(geometry: BufferGeometry): BufferGeometry {
  geometry.computeBoundingBox();
  const first = geometry.boundingBox;
  if (first === null) throw new Error('malha sem bounding box');

  const height = first.max.y - first.min.y;
  if (height <= 0) throw new Error('malha sem altura');
  const scale = tokens.world.avatarHeight / height;
  geometry.scale(scale, scale, scale);

  // Recalculado de propósito: a caixa anterior é do tamanho de ANTES da escala.
  geometry.computeBoundingBox();
  const scaled = geometry.boundingBox;
  if (scaled === null) throw new Error('malha sem bounding box');
  geometry.translate(
    -(scaled.max.x + scaled.min.x) / 2,
    -scaled.min.y,
    -(scaled.max.z + scaled.min.z) / 2,
  );

  // A malha de runtime vem sem normais (ver ADR-0016): o material usa
  // `flatShading`, que as deriva no fragmento. Sem bounding sphere, porém, o
  // frustum culling do three descarta o avatar em ângulo de câmera qualquer.
  geometry.computeBoundingSphere();
  return geometry;
}

/**
 * Busca o `.glb` e devolve a geometria pronta para uso, já normalizada.
 *
 * Devolve GEOMETRIA, e NEVER a cena carregada: quem decide material é o `World`,
 * porque material é papel (você, outro, caído) e papel NEVER está no arquivo.
 */
export async function loadAvatarGeometry(url: string): Promise<BufferGeometry> {
  const { GLTFLoader } = await import('three/examples/jsm/loaders/GLTFLoader.js');
  const gltf = await new GLTFLoader().loadAsync(url);

  // A quantização de atributo (KHR_mesh_quantization) deixa a escala de volta no
  // NÓ, não na malha. Sem aplicar a matriz do nó, o avatar apareceria com o
  // tamanho do inteiro de 16 bits — que é grande o bastante para sumir da tela.
  gltf.scene.updateWorldMatrix(true, true);

  let found: Mesh | undefined;
  gltf.scene.traverse((object) => {
    if (found === undefined && (object as Partial<Mesh>).isMesh === true) {
      found = object as Mesh;
    }
  });
  if (found === undefined) throw new Error(`nenhuma malha em ${url}`);

  const geometry = found.geometry.clone();
  geometry.applyMatrix4(found.matrixWorld);

  // O que veio do arquivo já não serve para nada: sem soltar, a malha original
  // fica no VRAM até a aba fechar.
  found.geometry.dispose();
  return normalizeAvatarGeometry(geometry);
}
